/**
 * WorkItemForms — LabResponseForm, TaskActionBar, ItemStatusBadge, EmptyBucket, _formatTime
 */
import React, { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Send, Check, Loader2, X, Plus, FlaskConical, Clock, User
} from 'lucide-react';
import { toast } from 'sonner';
import { BUCKET_CONFIG } from './constants';

export function LabResponseForm({ response, setResponse, routing, setRouting, onSubmit, submitting, itemId }) {
  const routingOptions = [
    { value: 'to_patient', label: 'Send to Patient' },
    { value: 'to_nurse', label: 'Send to Nurse' },
    { value: 'internal_note', label: 'Internal Note' },
  ];

  return (
    <div className="space-y-3" data-testid={`lab-response-form-${itemId}`}>
      <Textarea
        placeholder="Enter your response..."
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        className="min-h-[72px] text-sm resize-none"
        data-testid={`response-textarea-${itemId}`}
      />
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {routingOptions.map(opt => (
            <button
              key={opt.value}
              onClick={() => setRouting(opt.value)}
              className={`
                px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                ${routing === opt.value
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
                }
              `}
              data-testid={`routing-btn-${opt.value}-${itemId}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          onClick={onSubmit}
          disabled={!response.trim() || submitting}
          data-testid={`submit-response-btn-${itemId}`}
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
          Submit & Route
        </Button>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   TASK ACTION BAR — Bidirectional thread model
   Reply | Send to Nurse | File
   ═══════════════════════════════════════ */

export function TaskActionBar({ item, notes, setNotes, submitting, setSubmitting, onComplete, bucket }) {
  const [activeAction, setActiveAction] = useState(null); // 'reply' | 'nurse' | 'file'
  const [message, setMessage] = useState('');

  const isPendingNurse = item.status === 'pending_nurse';

  const doAction = async (endpoint, body) => {
    setSubmitting(true);
    try {
      await api.post(`/my-day/items/${item.id}/${endpoint}`, body);
      onComplete();
    } catch (err) {
      console.error(`${endpoint} failed:`, err);
    } finally {
      setSubmitting(false);
    }
  };

  const actions = [
    { key: 'reply', label: 'Reply', icon: Send, disabled: false },
    { key: 'nurse', label: 'Send to Nurse', icon: ArrowRight, disabled: false },
    { key: 'file', label: 'File', icon: Check, disabled: isPendingNurse },
  ];

  return (
    <div className="space-y-2" data-testid={`task-action-bar-${item.id}`}>
      {/* Action buttons */}
      <div className="flex gap-1.5">
        {actions.map(a => (
          <button
            key={a.key}
            onClick={() => setActiveAction(activeAction === a.key ? null : a.key)}
            disabled={a.disabled}
            className={`
              flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
              ${a.disabled ? 'opacity-40 cursor-not-allowed bg-muted text-muted-foreground' :
                activeAction === a.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}
            `}
            data-testid={`action-${a.key}-${item.id}`}
            title={a.disabled ? 'Pending nurse response — resolve thread first' : ''}
          >
            <a.icon className="w-3 h-3" />
            {a.label}
          </button>
        ))}
      </div>

      {/* Action-specific input */}
      {activeAction === 'reply' && (
        <div className="space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
          <Textarea placeholder="Reply to sender..." value={message} onChange={e => setMessage(e.target.value)}
            className="min-h-[56px] text-sm resize-none" data-testid={`reply-input-${item.id}`} />
          <div className="flex justify-end">
            <Button size="sm" onClick={() => doAction('reply', { message })} disabled={!message.trim() || submitting}
              data-testid={`reply-submit-${item.id}`}>
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Send Reply
            </Button>
          </div>
        </div>
      )}

      {activeAction === 'nurse' && (
        <div className="space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
          <Textarea placeholder="Message for nurse..." value={message} onChange={e => setMessage(e.target.value)}
            className="min-h-[56px] text-sm resize-none" data-testid={`nurse-msg-${item.id}`} />
          <div className="flex justify-end">
            <Button size="sm" onClick={() => doAction('send-to-nurse', { message })} disabled={submitting}
              data-testid={`nurse-submit-${item.id}`}>
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}
              Send to Nurse
            </Button>
          </div>
        </div>
      )}

      {activeAction === 'file' && (
        <div className="space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100">
          <Textarea placeholder="Filing notes (optional)..." value={notes} onChange={e => setNotes(e.target.value)}
            className="min-h-[56px] text-sm resize-none" data-testid={`file-notes-${item.id}`} />
          <div className="flex justify-end">
            <Button size="sm" onClick={() => doAction('file', { notes })} disabled={submitting}
              data-testid={`file-submit-${item.id}`}>
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              File to Chart
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   ITEM STATUS BADGE
   ═══════════════════════════════════════ */

export function ItemStatusBadge({ status }) {
  const cfg = {
    pending: null,
    pending_provider: { label: 'Your turn', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    pending_nurse: { label: 'With nurse', color: 'bg-blue-50 text-blue-600 border-blue-200' },
    in_progress: { label: 'In progress', color: 'bg-violet-50 text-violet-600 border-violet-200' },
    completed: { label: 'Filed', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  };
  const c = cfg[status];
  if (!c) return null;
  return <Badge variant="outline" className={`text-[9px] h-4 flex-shrink-0 ${c.color}`}>{c.label}</Badge>;
}


/* ═══════════════════════════════════════
   EMPTY BUCKET
   ═══════════════════════════════════════ */

export function EmptyBucket({ bucket, onSeed, seeding }) {
  const cfg = BUCKET_CONFIG[bucket];
  const Icon = cfg?.icon || FileText;
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-bucket">
      <div className="p-4 rounded-2xl bg-muted/50 mb-4">
        <Icon className="w-8 h-8 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">No pending {cfg?.label?.toLowerCase() || 'items'}</p>
      <p className="text-xs text-muted-foreground mb-4">All caught up!</p>
      <Button variant="outline" size="sm" onClick={onSeed} disabled={seeding} data-testid="empty-seed-btn">
        {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
        Load Demo Items
      </Button>
    </div>
  );
}


/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */


/* ═══════════════════════════════════════
   REFILL BUCKET VIEW — Patient-Centric (7H.1)
   ═══════════════════════════════════════ */


export function _formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}


/* ═══════════════════════════════════════
   QUICK CLINICAL COMMAND BOX (7G)
   ═══════════════════════════════════════ */

